import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AndroidIcon from '@mui/icons-material/Android';

import googlePlayBadge from '../assets/img/google-play-badge.png';
import appMockup from '../assets/img/app-mockup-woman.png';

const AndroidDownload = () => {
  const location = useLocation();

  // Scroll to top when component mounts or route changes
  useEffect(() => {
    // Scroll to top instantly when component loads
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' // Use 'instant' for immediate scroll
    });
    
    // Also add a slight delay scroll as backup
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [location.pathname]); // Re-run when route changes

  return (
    <div>
      <style jsx global>{`
        /* Reset body margin and padding for this page */
        body {
          margin: 0;
          padding: 0;
        }
        
        .android-download-section {
          background: linear-gradient(135deg, #fffbe6 0%, #f7f4e8 100%);
          padding: 100px 20px 80px;
          text-align: center;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          min-height: 100vh;
          box-sizing: border-box;
        }

        /* BEAUTIFUL HEADING */
        .section-heading {
          margin-bottom: 70px;
          padding-top: 20px; /* Extra padding at top */
        }
        .main-title {
          font-size: 52px;
          font-weight: 900;
          color: #222;
          margin: 0 0 16px;
          line-height: 1.1;
          animation: fadeInUp 0.8s ease-out;
        }
        .main-title span {
          color: #098535;
        }
        .subtitle {
          font-size: 20px;
          color: #555;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
          animation: fadeInUp 1s ease-out 0.2s both;
        }

        .features-bar {
          display: flex;
          justify-content: center;
          gap: 40px;
          flex-wrap: wrap;
          margin-bottom: 60px;
          font-size: 15px;
          color: #333;
        }
        .feature-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 600;
          animation: fadeInUp 1s ease-out 0.4s both;
        }
        .feature-item i {
          font-size: 20px;
          color: #098535;
        }

        .download-cards {
          display: flex;
          justify-content: center;
          gap: 40px;
          flex-wrap: wrap;
          max-width: 1200px;
          margin: 0 auto;
        }
        .download-card {
          background: white;
          border-radius: 28px;
          padding: 40px 30px;
          width: 380px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.08);
          transition: all 0.4s ease;
          position: relative;
          overflow: hidden;
          animation: fadeInUp 1s ease-out 0.6s both;
        }
        .download-card:hover {
          transform: translateY(-12px);
          box-shadow: 0 30px 70px rgba(0,0,0,0.15);
        }
        .app-logo {
          width: 60px;
          height: 60px;
          background: #098535;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          color: white;
          font-weight: bold;
          margin-bottom: 16px;
        }
        .app-name {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .rating {
          color: #ffa800;
          font-weight: 600;
          margin-bottom: 24px;
        }
        .download-title {
          font-size: 28px;
          font-weight: 800;
          margin: 30px 0 40px;
          line-height: 1.2;
        }

        .download-action-btn {
          background: #000 !important;
          color: white;
          padding: 16px 32px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          gap: 14px;
          font-size: 18px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 8px 20px rgba(0,0,0,0.2);
          border: none;
          cursor: pointer;
          height: 68px;
        }
        .download-action-btn:hover {
          background: #000 !important;
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(0,0,0,0.35);
        }
        .download-action-btn:hover span,
        .download-action-btn:hover svg {
          color: #098535 !important;
          fill: #098535 !important;
        }
        .download-action-btn img { height: 40px; }
        .download-action-btn svg {
          font-size: 40px;
          color: white;
          transition: color 0.3s ease;
        }

        .mockup-img {
          max-width: 100%;
          height: auto;
          margin: -60px 0 -30px;
          pointer-events: none;
        }
        .green-accent {
          position: absolute;
          top: -60px;
          right: -60px;
          width: 200px;
          height: 200px;
          background: #098535;
          border-radius: 50%;
          opacity: 0.1;
        }

        /* Animations */
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 992px) {
          .main-title { font-size: 44px; }
        }
        @media (max-width: 768px) {
          .main-title { font-size: 38px; }
          .subtitle { font-size: 18px; }
        }
        @media (max-width: 576px) {
          .main-title { font-size: 32px; }
          .download-cards { flex-direction: column; align-items: center; }
          .download-card { width: 90%; max-width: 380px; }
        }
      `}</style>

      <section className="android-download-section">
        {/* BEAUTIFUL HEADING */}
        <div className="section-heading">
          <h1 className="main-title">
            Get the <span>CabandGo</span> App
          </h1>
          <p className="subtitle">
            Book rides faster, track your driver live, pay securely, and enjoy exclusive app-only offers!
          </p>
        </div>

        {/* Features */}
        <div className="features-bar">
          <div className="feature-item">Pricing control</div>
          <div className="feature-item">Route visibility</div>
          <div className="feature-item">Freedom to choose</div>
          <div className="feature-item">Safety and support</div>
        </div>

        {/* Download Cards */}
        <div className="download-cards">
          {/* Google Play */}
          <div className="download-card">
            <div className="app-logo">C</div>
            <div className="app-name">CabandGo App</div>
            <div className="rating">4.7/5</div>
            <h3 className="download-title">Download from<br />Google Play</h3>
            <a 
              href="https://play.google.com/store/apps/details?id=com.cabandgo.app" 
              className="download-action-btn" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <img src={googlePlayBadge} alt="Google Play" />
              <span>Get it on<br /><strong>Google Play</strong></span>
            </a>
          </div>

          {/* Direct APK */}
          <div className="download-card" style={{position: 'relative'}}>
            <div className="green-accent"></div>
            <div className="app-logo">C</div>
            <div className="app-name">CabandGo App</div>
            <div className="rating">4.7/5</div>
            <img src={appMockup} alt="CabandGo App" className="mockup-img" />
            <h3 className="download-title">Download from<br />our website</h3>
            <a 
              href="/downloads/cabandgo-v1.0.apk" 
              download="CabandGo.apk"
              className="download-action-btn"
            >
              <AndroidIcon />
              <span>Download<br /><strong>Android App</strong></span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AndroidDownload;